import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { escrowApi } from "@/api/escrow";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency } from "@/components/deals/dealFormat";
import type { EscrowTransactionDetailDto } from "@/types/escrow";

interface Props {
  open: boolean;
  escrow: EscrowTransactionDetailDto | null;
  onClose: () => void;
  onApproved?: () => void;
}

/**
 * Confirms approval of a draft escrow transaction. Approval fires the
 * Escrow.com create-transaction call (which charges fees to the buyer and
 * notifies them), so this is a deliberate two-step gate.
 */
export function ApproveEscrowModal({ open, escrow, onClose, onApproved }: Props) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => {
      if (!escrow) throw new Error("No escrow transaction selected.");
      return escrowApi.approve(escrow.id);
    },
    onSuccess: () => {
      toast.success("Escrow transaction approved and sent to Escrow.com.");
      queryClient.invalidateQueries({ queryKey: ["escrow"] });
      onApproved?.();
      onClose();
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  if (!escrow) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Approve & send to Escrow.com"
      description={escrow.dealNumber ?? undefined}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>
            Approve & send
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <p className="text-small text-gray-mid">
          This creates the transaction on Escrow.com. The buyer is charged all
          platform fees and is notified by Escrow.com. This cannot be undone from
          Phoenix.
        </p>
        <dl className="flex flex-col gap-space-2 text-small">
          <Row label="Amount" value={`${formatCurrency(escrow.amount)} ${escrow.currency}`} />
          <Row label="Description" value={escrow.itemDescription} />
          <Row label="Buyer" value="Pulled from the deal's borrower" />
          <Row label="Counterparty" value="Phoenix Capital (seller, 100% beneficiary)" />
          <Row label="Fees" value="Buyer pays all Escrow.com fees" />
        </dl>
      </div>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-space-4">
      <dt className="text-gray-mid">{label}</dt>
      <dd className="text-onyx text-right font-medium">{value}</dd>
    </div>
  );
}
